'use client'

import { useEffect, useRef } from 'react'
import type { Metadata } from "next";
import { usePathname } from 'next/navigation'
import { SpeedInsights } from '@vercel/speed-insights/next'
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import "@/app/globals.css";
import "@/lib/i18n";
// TEMPORARILY DISABLED - Service Worker causing issues
// import { registerServiceWorker } from "@/lib/push-notifications";

// Note: Metadata export doesn't work with 'use client', but we need client for AuthProvider
// In production, you might want to split this into server/client components

function PushNotificationInitializer() {
  const { user } = useAuth()

  useEffect(() => {
    // TEMPORARILY DISABLED - Service Worker causing cache issues
    // Initialize service worker and push notifications when user is logged in
    // if (user && typeof window !== 'undefined' && 'serviceWorker' in navigator) {
    //   registerServiceWorker().catch(console.error)
    // }
  }, [user])

  return null
}

function DatePickerEnhancer() {
  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleDateInputFocus = (e: FocusEvent) => {
      const target = e.target as HTMLInputElement
      if (target && (target.type === 'date' || target.type === 'datetime-local')) {
        // Apply zoom to body to make calendar popup larger
        // This is a workaround since native calendar can't be styled directly
        // Using smaller zoom (1.25) to be less invasive
        document.body.style.zoom = '1.25'
      }
    }

    const handleDateInputBlur = (e: FocusEvent) => {
      const target = e.target as HTMLInputElement
      if (target && (target.type === 'date' || target.type === 'datetime-local')) {
        // Reset zoom after a delay to allow calendar to close
        setTimeout(() => {
          document.body.style.zoom = '1'
        }, 300)
      }
    }

    // Also handle click events for calendar picker indicator
    const handleDateInputClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const input = target.closest('input[type="date"], input[type="datetime-local"]') as HTMLInputElement
      if (input) {
        document.body.style.zoom = '1.25'
      }
    }

    document.addEventListener('focusin', handleDateInputFocus)
    document.addEventListener('focusout', handleDateInputBlur)
    document.addEventListener('click', handleDateInputClick)

    return () => {
      document.removeEventListener('focusin', handleDateInputFocus)
      document.removeEventListener('focusout', handleDateInputBlur)
      document.removeEventListener('click', handleDateInputClick)
      // Reset on cleanup
      document.body.style.zoom = '1'
    }
  }, [])

  return null
}

function RouteProtection({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const prevPathnameRef = useRef(pathname)
  const navigationStartRef = useRef<number | null>(null)
  
  // Track pathname changes (navigation)
  useEffect(() => {
    if (prevPathnameRef.current !== pathname) {
      const navigationTime = navigationStartRef.current ? Date.now() - navigationStartRef.current : 0
      prevPathnameRef.current = pathname
      navigationStartRef.current = Date.now()
    }
  }, [pathname])
  
  // Preserve router cache when tab loses focus
  useEffect(() => {
    if (typeof window === 'undefined') return
    
    const handleVisibilityChange = () => {
      const timestamp = new Date().toISOString()
      if (document.hidden) {
        // Tab lost focus - preserve router cache
        // Next.js App Router should keep cache, but we ensure it
        // Router cache is preserved automatically by Next.js, but we log for debugging
        console.log(`[Layout] Tab lost focus - router cache should be preserved at ${timestamp}, pathname=${pathname}`)
      } else {
        // Tab gained focus - router cache should still be available
        // Next.js App Router maintains router cache across tab switches
        const tabGainFocusTime = Date.now()
        console.log(`[Layout] Tab gained focus - router cache should be available at ${timestamp}, pathname=${pathname}`)
        navigationStartRef.current = Date.now()
        
        // Log after a short delay to see if components re-render
        setTimeout(() => {
          const timeSinceFocus = Date.now() - tabGainFocusTime
          void timeSinceFocus
        }, 100)
      }
    }
    
    if (typeof window !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange)
      return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [pathname])
  
  return <ProtectedRoute>{children}</ProtectedRoute>
}

export default function RootLayout({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  return (
    <html lang="pl">
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes" />
        <meta name="description" content="System CRM do obsługi imprez okolicznościowych" />
        <meta name="theme-color" content="#000000" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <meta name="apple-mobile-web-app-title" content="Banquieto" />
        <link rel="manifest" href="/manifest.json" />
        <link rel="icon" type="image/png" href="/favicon%20banquieto.png?v=2" />
        <link rel="shortcut icon" type="image/png" href="/favicon%20banquieto.png?v=2" />
        <link rel="apple-touch-icon" href="/favicon%20banquieto.png?v=2" />
        {/* Fallback for browsers that request favicon.ico */}
        <link rel="icon" type="image/png" href="/favicon%20banquieto.png?v=2" sizes="any" />
        {/* Preload critical resources */}
        <link rel="preload" href="/logo%20banquieto.png" as="image" type="image/png" />
        {/* Cache-Control headers for better caching */}
        <meta httpEquiv="Cache-Control" content="public, max-age=31536000, immutable" />
        <title>Banquieto</title>
        {/* DISABLED: unregister-sw.js was clearing all caches on every page load, causing slow loading after tab switch */}
        {/* <script src="/unregister-sw.js" defer /> */}
      </head>
      <body>
        <AuthProvider>
          <PushNotificationInitializer />
          <DatePickerEnhancer />
          <RouteProtection>
            {children}
          </RouteProtection>
        </AuthProvider>
        <SpeedInsights />
      </body>
    </html>
  );
}
